<template>
  <div class="proapp-intelligence" :class="{'proapp-intelligence--open': open}">
    <transition name="intelligence-panel-transition">
      <ProAppIntelligencePanel
        v-if="open"
        :expanded="expanded"
        :messages="messages"
        :thinking="thinking"
        :responding="responding"
        :suggestions="intelligenceSettings.suggestions"
        :introduction="introduction"
        :settings="intelligenceSettings"
        :settings-open="settingsOpen"
        :reset-key="resetKey"
        @send="sendMessage"
        @reset="resetConversation"
        @toggle-settings="toggleSettings"
        @change-settings="updateSettings"
        @restore-settings="restoreSettings"
        @toggle-expand="expanded = !expanded"
        @close="open = false"
      />
    </transition>
    <div class="proapp-intelligence__trigger">
      <ProAppIntelligenceFloatingButton :open="open" @click="open = !open" />
    </div>
  </div>
</template>

<script>
import ProAppIntelligenceFloatingButton from './ProAppIntelligenceFloatingButton'
import ProAppIntelligencePanel from './ProAppIntelligencePanel'
import {
  getMockResponse,
  getIntroduction,
  presentationTiming,
} from '~/configs/proAppIntelligence/mockConversations'
import {
  getDefaultIntelligenceSettings,
  loadIntelligenceSettings,
  saveIntelligenceSettings,
} from '~/configs/proAppIntelligence/localSettings'

export default {
  name: 'ProAppIntelligence',
  components: {
    ProAppIntelligenceFloatingButton,
    ProAppIntelligencePanel,
  },
  data: () => ({
    open: false,
    expanded: false,
    thinking: false,
    responding: false,
    settingsOpen: false,
    messages: [],
    timers: [],
    greetingTimer: null,
    nextMessageId: 1,
    resetKey: 0,
    introduction: getIntroduction(),
    intelligenceSettings: getDefaultIntelligenceSettings(),
  }),
  mounted() {
    this.intelligenceSettings = loadIntelligenceSettings()
    this.greetingTimer = window.setInterval(this.updateGreeting, 60000)
  },
  beforeDestroy() {
    this.clearTimers()
    window.clearInterval(this.greetingTimer)
  },
  methods: {
    updateGreeting() {
      this.introduction = getIntroduction()
    },
    toggleSettings() {
      if (this.messages.length) return

      if (this.settingsOpen) {
        const settings = loadIntelligenceSettings()
        this.updateSettings(settings)
      }

      this.settingsOpen = !this.settingsOpen
    },
    updateSettings(settings) {
      this.intelligenceSettings = settings
      saveIntelligenceSettings(settings)
    },
    restoreSettings() {
      const settings = getDefaultIntelligenceSettings()
      this.updateSettings(settings)
    },
    createMessage(role, content) {
      return {
        id: `proapp-intelligence-${this.nextMessageId++}`,
        role,
        content,
        createdAt: new Date().toISOString(),
      }
    },
    schedule(callback, delay) {
      const timer = window.setTimeout(() => {
        this.timers = this.timers.filter((item) => item !== timer)
        callback()
      }, delay)
      this.timers.push(timer)
    },
    sendMessage(content) {
      const normalizedContent = String(content || '').trim()
      if (!normalizedContent || this.responding) return

      const mockResponse = getMockResponse(
        normalizedContent,
        this.intelligenceSettings.suggestions,
        this.intelligenceSettings.keywordResponses,
        this.intelligenceSettings.keywordStatuses,
      )
      this.messages.push(this.createMessage('user', normalizedContent))
      this.thinking = true
      this.responding = true

      const statuses = mockResponse.statuses || []
      let statusMessage = null
      statuses.forEach((status, index) => {
        this.schedule(
          () => {
            if (statusMessage) {
              statusMessage.content = status
              return
            }

            statusMessage = this.createMessage('system', status)
            this.messages.push(statusMessage)
          },
          presentationTiming.firstStatusDelay +
            index * presentationTiming.statusInterval,
        )
      })

      const responseDelay = statuses.length
        ? presentationTiming.firstStatusDelay +
          (statuses.length - 1) * presentationTiming.statusInterval +
          presentationTiming.responseStartDelay
        : presentationTiming.responseStartDelay

      this.schedule(() => {
        if (statusMessage) {
          this.messages = this.messages.filter(
            (message) => message.id !== statusMessage.id,
          )
          statusMessage = null
        }
        this.thinking = false
        this.streamAssistantResponse(mockResponse.response)
      }, responseDelay)
    },
    streamAssistantResponse(content) {
      const words = content.match(/\S+\s*/g) || []
      const message = {
        ...this.createMessage('assistant', ''),
        isStreaming: true,
      }
      this.messages.push(message)

      const typeWord = (wordIndex) => {
        if (wordIndex >= words.length) {
          message.isStreaming = false
          message.createdAt = new Date().toISOString()
          this.responding = false
          return
        }

        message.content += words[wordIndex]
        const delay =
          presentationTiming.wordDelay +
          (wordIndex % 3) * presentationTiming.wordDelayVariation
        this.schedule(() => typeWord(wordIndex + 1), delay)
      }

      typeWord(0)
    },
    resetConversation() {
      this.clearTimers()
      this.messages = []
      this.settingsOpen = false
      this.thinking = false
      this.responding = false
      this.nextMessageId = 1
      this.resetKey++
    },
    clearTimers() {
      this.timers.forEach((timer) => window.clearTimeout(timer))
      this.timers = []
    },
  },
}
</script>

<style lang="scss" scoped>
.proapp-intelligence {
  position: relative;
  z-index: 190;

  &__trigger {
    position: fixed;
    right: 10px;
    bottom: 20px;
  }
}

.intelligence-panel-transition-enter-active,
.intelligence-panel-transition-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
  transform-origin: bottom right;
}

.intelligence-panel-transition-enter,
.intelligence-panel-transition-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

@media screen and (max-width: 999px) {
  .proapp-intelligence--open .proapp-intelligence__trigger {
    display: none;
  }
}

@media screen and (max-width: 450px) {
  .proapp-intelligence__trigger {
    right: 10px;
    bottom: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .intelligence-panel-transition-enter-active,
  .intelligence-panel-transition-leave-active {
    transition: none;
  }
}
</style>
